import { useState } from 'preact/hooks';
import type { EpisodeDto } from '../../shared/types';
import { formatDate, formatDurationWords } from '../lib/intl';
import { currentEpisode, formatTime, isPlaying, playEpisode, togglePlay } from '../state/player';
import { addToQueue } from '../state/queue';
import { isSignedIn } from '../state/session';
import { AddToPlaylistModal } from './AddToPlaylistModal';
import { Artwork } from './Artwork';
import { DownloadButton } from './DownloadButton';
import { Icon } from './Icon';
import { PlayingIndicator } from './PlayingIndicator';

interface Props {
  episode: EpisodeDto;
  showArtwork?: boolean;
  showPodcast?: boolean;
}

export function EpisodeRow({ episode, showArtwork = true, showPodcast = false }: Props) {
  const [queued, setQueued] = useState(false);
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const isCurrent = currentEpisode.value?.id === episode.id;
  const progress = episode.progress;
  const played = progress?.completed === true;
  const listenedFraction =
    progress && progress.durationSec ? progress.positionSec / progress.durationSec : 0;

  const remaining =
    progress && !progress.completed && progress.positionSec > 0
      ? `${formatTime((episode.durationSec ?? progress.durationSec ?? 0) - progress.positionSec)} left`
      : formatTime(episode.durationSec);

  /*
   * Names this row's thumbnail as the same object as the hero artwork on the episode page,
   * so the browser morphs one into the other across the navigation instead of cross-fading
   * two unrelated rectangles.
   *
   * A name must be unique within a snapshot — two elements sharing one aborts the whole
   * transition, not just the morph. Two things keep that true: only the playing row claims a
   * name, and no screen lists one episode twice (Home de-duplicates its two sections, which
   * do legitimately overlap — see `views/Home.tsx`). Should a third list ever break that,
   * fix the duplicate listing rather than reintroducing a claim registry here: showing the
   * same episode twice on one screen is a bug in its own right.
   */
  const morphName = isCurrent ? { viewTransitionName: `art-${episode.id}` } : undefined;

  // Optimistic: the tick appears on the press, and only a server refusal takes it back.
  const enqueue = async () => {
    setQueued(true);
    if (!(await addToQueue(episode))) setQueued(false);
  };

  return (
    <article class={`episode-row ${played ? 'is-played' : ''} ${isCurrent ? 'is-current' : ''}`}>
      {showArtwork && (
        <a
          href={`/episode/${episode.id}`}
          class="episode-row__art"
          aria-hidden="true"
          tabIndex={-1}
          style={morphName}
        >
          <Artwork src={episode.imageUrl ?? episode.feedImageUrl} alt="" size={64} />
        </a>
      )}

      <div class="episode-row__body">
        {showPodcast && episode.feedTitle && (
          <a class="episode-row__show" href={`/podcast/${episode.feedId}`}>
            {episode.feedTitle}
          </a>
        )}

        <h3 class="episode-row__title">
          <a href={`/episode/${episode.id}`}>{episode.title}</a>
        </h3>

        <p class="episode-row__meta">
          {episode.pubDate && (
            <time dateTime={new Date(episode.pubDate).toISOString()}>
              {formatDate(episode.pubDate)}
            </time>
          )}
          <span aria-hidden="true"> · </span>
          {/*
            Sighted readers get the clock face ("42:10 left"); a screen reader gets the same
            duration spoken as words, because "four two colon one zero" is not a duration.
          */}
          <span aria-label={formatDurationWords(episode.durationSec ?? progress?.durationSec)}>
            {remaining}
          </span>
          {episode.season !== null && episode.episodeNum !== null && (
            <>
              <span aria-hidden="true"> · </span>
              <span>
                S{episode.season} E{episode.episodeNum}
              </span>
            </>
          )}
          {episode.transcripts.length > 0 && <span class="badge">Transcript</span>}
          {episode.chaptersUrl && <span class="badge">Chapters</span>}
          {episode.explicit && <span class="badge badge--warn">Explicit</span>}
        </p>

        {listenedFraction > 0 && !played && (
          <div
            class="episode-row__progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(listenedFraction * 100)}
            aria-label="Listening progress"
          >
            <div style={{ width: `${Math.min(100, listenedFraction * 100)}%` }} />
          </div>
        )}
      </div>

      <div class="episode-row__actions">
        {isCurrent && <PlayingIndicator />}

        <button
          type="button"
          class="icon-button icon-button--primary"
          onClick={() => (isCurrent ? togglePlay() : playEpisode(episode))}
          aria-label={
            isCurrent && isPlaying.value ? `Pause ${episode.title}` : `Play ${episode.title}`
          }
        >
          <Icon name={isCurrent && isPlaying.value ? 'pause' : 'play'} size={20} />
        </button>

        {isSignedIn.value && (
          <>
            <button
              type="button"
              class="icon-button"
              onClick={enqueue}
              disabled={queued}
              aria-label={queued ? 'Added to queue' : `Add ${episode.title} to queue`}
            >
              <Icon name={queued ? 'check' : 'plus'} size={20} />
            </button>
            <button
              type="button"
              class="icon-button"
              onClick={() => setShowPlaylistModal(true)}
              aria-label={`Add ${episode.title} to playlist`}
              title="Add to playlist"
            >
              <Icon name="queue" size={20} />
            </button>
          </>
        )}

        <DownloadButton episode={episode} />
      </div>

      {showPlaylistModal && (
        <AddToPlaylistModal episode={episode} onClose={() => setShowPlaylistModal(false)} />
      )}
    </article>
  );
}
